import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import type { ClientEntityByName, ClientTableName } from '@app/icare/client/db/types'
import { appFormOptions } from '@app/icare/form/appFormOptions'
import { useAppForm } from '@app/icare/form/useAppForm'
import type { EntityId } from '@pkg/db/schema'
import type { SectionDef } from '@pkg/form/index'
import { useSectionFlow } from '@pkg/form/index'
import { useAppNiceModal } from '@pkg/ui/modal'
import { useStore } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import type { z } from 'zod'

type DrawerMode = 'create' | 'edit'

interface UseEntityDrawerArgs<
  T extends ClientTableName,
  Schema extends z.ZodType<ClientEntityByName<T>>,
  Sections extends readonly SectionDef[],
> {
  schema: Schema
  entityId: EntityId<T> | undefined
  sections: Sections
  section: Sections[number]['name'] | undefined
  formDefaults?: Partial<z.output<Schema>>
  onPersist: (value: z.output<Schema>) => Promise<unknown> | unknown
  onAfterCommit?: (ctx: { value: z.output<Schema>; mode: DrawerMode }) => Promise<void> | void
  canProceed?: (form: unknown, section: Sections[number]['name']) => boolean
}

export function useEntityDrawer<
  T extends ClientTableName,
  Schema extends z.ZodType<ClientEntityByName<T>>,
  Sections extends readonly SectionDef[],
>(args: UseEntityDrawerArgs<T, Schema, Sections>) {
  const { schema, entityId, sections, section, formDefaults, onPersist, onAfterCommit } = args

  const modal = useAppNiceModal()
  const mode: DrawerMode = entityId ? 'edit' : 'create'

  const entityQueryOptions = localQueryEntityOptions<T>(entityId)

  const { data: existing, isLoading, error } = useQuery(entityQueryOptions)

  const persistMutation = useMutation({
    mutationFn: async (value: z.output<Schema>) => onPersist(value),
  })

  const form = useAppForm(
    appFormOptions(schema, {
      defaultValues: (existing ?? formDefaults) as z.output<Schema>,
      onSubmit: async ({ value }) => {
        await persistMutation.mutateAsync(value)
        modal.resolveRemove({ value, mode })
        await onAfterCommit?.({ value, mode })
      },
    }),
  )

  const isDirty = useStore(form.store, s => s.isDirty)
  const isSaving = useStore(form.store, s => s.isSubmitting)
  const canSubmit = useStore(form.store, s => s.isFormValid)

  const flow = useSectionFlow({
    sections,
    section,
    mode: entityId ? 'tabbed' : 'sequential',
  })

  const handleCancel = useCallback(() => {
    modal.rejectRemove()
  }, [modal])

  const handleSubmit = useCallback(() => {
    void form.handleSubmit()
  }, [form])

  return {
    modal,
    mode,
    entity: existing,
    form,
    flow,
    isDirty,
    isSaving: isSaving || persistMutation.isPending,
    canSubmit,
    canProceed: args.canProceed ? args.canProceed(form as unknown, flow.active) : undefined,
    isLoading,
    error,
    persistError: persistMutation.error,
    persistMutation,
    handleCancel,
    handleSubmit,
  }
}

export type EntityDrawer<
  T extends ClientTableName,
  Schema extends z.ZodType<ClientEntityByName<T>>,
  Sections extends readonly SectionDef[],
> = ReturnType<typeof useEntityDrawer<T, Schema, Sections>>
